import { toast } from 'sonner';

/** One cancelable automatic-print admission for an already-published document. */
export function scheduleAutomaticPrint({
  popup,
  isCurrent = () => true,
  onSettled = () => {},
  beforePrint = () => {},
  image = null,
  initialDelay = 150,
  imageDelay = 100,
  imageDeadline = 2000,
  afterFitDelay = 0,
}) {
  let settled = false;
  let admitted = false;
  const timers = new Set();
  let monitor;

  const current = () => {
    try { return !popup.closed && isCurrent(); } catch { return false; }
  };
  const clear = () => {
    timers.forEach(clearTimeout);
    timers.clear();
    clearInterval(monitor);
    image?.removeEventListener('load', imageReady);
    image?.removeEventListener('error', imageReady);
    popup.removeEventListener?.('pagehide', cancelAutoPrint);
  };
  const finish = () => {
    if (settled) return;
    settled = true;
    clear();
    onSettled();
  };
  function cancelAutoPrint() { finish(); }
  const later = (callback, delay) => {
    const timer = setTimeout(() => {
      timers.delete(timer);
      callback();
    }, delay);
    timers.add(timer);
  };
  const print = () => {
    if (settled) return;
    if (!current()) { finish(); return; }
    // Settle internally before invocation: exceptions or reentrant events must
    // never reopen admission. The owner is notified after this attempt finishes.
    settled = true;
    clear();
    try { popup.focus(); } catch { /* Focusing is best-effort; printing may still work. */ }
    try {
      if (current()) popup.print();
    } catch {
      toast.error('Automatic printing could not start. Use Print in the prepared window.');
    } finally {
      onSettled();
    }
  };
  const fitThenPrint = () => {
    if (settled || admitted) return;
    admitted = true;
    // Logo load, error and deadline compete here, not at window.print().
    timers.forEach(clearTimeout);
    timers.clear();
    image?.removeEventListener('load', imageReady);
    image?.removeEventListener('error', imageReady);
    if (!current()) { finish(); return; }
    try { beforePrint(); } catch {
      toast.error('Automatic printing could not start. Use Print in the prepared window.');
      finish();
      return;
    }
    later(print, afterFitDelay);
  };
  function imageReady() {
    if (!settled && !admitted) later(fitThenPrint, imageDelay);
  }

  popup.addEventListener?.('pagehide', cancelAutoPrint);
  monitor = setInterval(() => { if (!current()) finish(); }, 100);
  if (!current()) finish();
  else if (!image) later(fitThenPrint, initialDelay);
  else if (image.complete) later(fitThenPrint, imageDelay);
  else {
    image.addEventListener('load', imageReady);
    image.addEventListener('error', imageReady);
    later(fitThenPrint, imageDeadline);
  }
  return { cancelAutoPrint };
}
